import { Box, Flex, Grid, GridItem, Tooltip } from '@chakra-ui/react';
import ComposeEmail from 'src/components/EmailModule';
import SubmissionInfoWithAuthors from '../SubmissionInfoWithAuthors';
import Loading from '../../../../pages/Status/Loading';
import { EmailTemplateDoc } from '@publishing-engine/domain/models/email-templates';
import ReviewersListSelectionAccountCreation from '../ReviewerSelection/ReviewersListSelectionAccountCreation';
import { Text } from '@chakra-ui/react';
import { ButtonText } from '@morressier/alembic';
import { useReviewerInviteList } from '../../../../contexts/ReviewerInviteListProvider';
import { useSubmissionData } from '../../../../contexts/SubmissionDataProvider';
import { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { COCSubmissionAssignment } from '../../../../pages/SubmissionDetails/COCSubmissionAssignment';

interface Props {
  goToPreviousStep: () => void;
  accountCreationEnabled: boolean;
  adHocEmail?: boolean;
  removeBrowseTemplates?: boolean;
  emailTemplates: EmailTemplateDoc[] | undefined;
  attachments?: any[];
  isLoadingTemplates: boolean;
}

const ReviewerInvitationEmailSelection = ({
  goToPreviousStep,
  accountCreationEnabled,
  adHocEmail = false,
  removeBrowseTemplates = false,
  emailTemplates,
  attachments = [],
  isLoadingTemplates
}: Props) => {
  const {
    workflowResponse: { submissionId }
  } = useSubmissionData();
  const { reviewerList, selectedReviewersToInvite } = useReviewerInviteList();

  const emailsToInvite = useMemo(() => {
    return reviewerList
      .map(r =>
        selectedReviewersToInvite[r.email] && !r.invited ? r.email : null
      )
      .filter((e): e is string => Boolean(e));
  }, [reviewerList, selectedReviewersToInvite]);

  if (isLoadingTemplates) return <Loading />;

  return (
    <Grid templateColumns="repeat(12, 1fr)" gap={2}>
      <GridItem colSpan={12}>
        <ButtonText variant="primary" onClick={goToPreviousStep}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </ButtonText>
        <Flex alignItems="center">
          <Text textStyle="headingSection">
            {adHocEmail ? 'Compose Email' : 'Send Email Notification'}
          </Text>
          <Tooltip
            hasArrow
            shouldWrapChildren
            placement="top"
            label={
              <Text textStyle="textContext">
                {adHocEmail
                  ? 'Compose your email, or select a template to pre-fill the subject and body. Submission details will be included automatically'
                  : 'Use the default template to invite reviewers, or customize the invitation’s subject, personal, and journal information. Submission details are always included automatically'}
              </Text>
            }
            borderRadius={4}
            data-cy="info-tooltip"
          >
            <ButtonText>
              <FontAwesomeIcon icon={faInfoCircle} />
            </ButtonText>
          </Tooltip>
        </Flex>
      </GridItem>

      <GridItem colSpan={8}>
        <Box mb={2}>
          <SubmissionInfoWithAuthors />
        </Box>
        <ComposeEmail
          emailTemplates={emailTemplates}
          adHocEmail={adHocEmail}
          removeBrowseTemplates={removeBrowseTemplates}
          emails={emailsToInvite}
          attachments={attachments}
        />
      </GridItem>
      <GridItem colSpan={4}>
        <Box mb={2}>
          <COCSubmissionAssignment
            submissionId={submissionId}
            readOnly
            defaultExpanded={false}
          />
        </Box>
        <ReviewersListSelectionAccountCreation
          isAccountCreationEnabled={accountCreationEnabled}
        />
      </GridItem>
    </Grid>
  );
};

export default ReviewerInvitationEmailSelection;
